/**
 * DealQuickViewModal — Customer 360 Sprint (Day 3)
 *
 * Lightweight in-modal deal preview, used to fix the Activity-tab asymmetry
 * in CustomerDetailModal: jobs + faults already open in-modal, but deals were
 * opening via `window.open(_, '_blank')` and losing context. This component
 * gives deals the same in-modal treatment.
 *
 * Props are pinned: `{ dealId, onOpenFullWorkspace, onClose }`.
 *   - Fetches the deal by id internally via `useDeal()` (matches the
 *     `JobDetailModal` / `FaultDetailsView` pattern — they receive a job/fault
 *     prop but the customer modal also fetches things itself).
 *   - "Open full workspace" navigates SAME-TAB. That's the whole point of
 *     this day — killing the new-tab asymmetry that broke the user's context.
 *
 * Shows: title, stage badge, value, monthly recurring, and the last few
 * activityLog entries. Not a full workspace — just enough context to decide
 * whether to drill in.
 *
 * z-index: z-[455] sits above JobDetailModal (z-[450]) but below
 * FaultDetailsView (z-[460]), preserving the relative ordering of nested
 * modals inside CustomerDetailModal. All three nested modals also stack
 * above the provider-mounted CustomerDetailModal at z-[400], so the
 * "customer-to-deal-to-customer" round-trip works without hiding anything.
 */

import { getLocaleSync } from '@/utils/locale';
import { getTenantTaxRateSync } from '@/utils/taxRate';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, DollarSign, Clock, Sparkles } from 'lucide-react';
import { useDeal } from '@/hooks/useDeals';
import { DEAL_STAGE_LABELS } from '@/types/theo-workflow.types';
import { formatCurrency } from '@/utils/pricing';

interface DealQuickViewModalProps {
  /** Deal doc id to preview. null = closed. */
  dealId: string | null;
  /** Same-tab navigation to the full workspace. Caller decides exact route. */
  onOpenFullWorkspace: (dealId: string) => void;
  /** Close the quick view. */
  onClose: () => void;
}

const MAX_ACTIVITY_ENTRIES = 5;

export function DealQuickViewModal({ dealId, onOpenFullWorkspace, onClose }: DealQuickViewModalProps) {
  const { deal, loading } = useDeal(dealId);

  if (!dealId) return null;

  const stageLabel = deal?.stage
    ? DEAL_STAGE_LABELS[deal.stage as keyof typeof DEAL_STAGE_LABELS] ?? deal.stage
    : '';

  const activity = (deal?.activityLog ?? []).slice(-MAX_ACTIVITY_ENTRIES).reverse();

  const handleOpenWorkspace = () => {
    if (!deal?.id) return;
    // Default behaviour: same-tab navigation. Callers can pass a custom
    // onOpenFullWorkspace if they want to do something else (e.g. close the
    // customer modal first), but the standard path is `navigate('/home/deals/:id')`.
    onOpenFullWorkspace(deal.id);
    onClose();
  };

  return (
    // mode="wait" so the previous deal's exit animation completes before
    // the next deal's enter starts. `key={dealId}` on the motion.div tells
    // AnimatePresence to treat each deal as a distinct child, triggering
    // proper exit/enter transitions when the user switches between deals
    // in the Activity tab. Bonus: keying also unmounts the inner component
    // tree (including the useDeal() hook), which kills the stale-data race
    // — A's in-flight fetch can no longer call setDeal on an unmounted B.
    <AnimatePresence mode="wait">
      {dealId && (
        <motion.div
          key={dealId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Match the inner card transition so backdrop + card fade together
          // and the blank-screen gap during mode="wait" deal switches stays
          // short. Default framer transition is ~300ms which feels janky.
          transition={{ duration: 0.15 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[455] flex items-center justify-center p-4"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Deal quick view"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-surface-card border border-theme rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-theme-soft flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-theme-tertiary mb-1">
                  Deal preview
                </p>
                <h2 className="text-lg font-bold text-theme-primary truncate leading-tight">
                  {deal?.title || deal?.customerName || (loading ? 'Loading…' : 'Untitled deal')}
                </h2>
                {stageLabel && (
                  <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-secondary/15 text-brand-secondary">
                    {stageLabel}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                title="Close (Esc)"
                aria-label="Close deal preview"
                className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 text-theme-muted" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading && !deal ? (
                <div className="space-y-2">
                  <div className="h-4 bg-surface-hover rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-surface-hover rounded animate-pulse w-1/2" />
                  <div className="h-20 bg-surface-hover rounded animate-pulse mt-4" />
                </div>
              ) : !deal ? (
                <p className="text-sm text-theme-tertiary">Deal not found.</p>
              ) : (
                <>
                  {/* Value strip */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {deal.value != null && deal.value > 0 && (
                      <div className="rounded-lg border border-theme-soft px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-theme-tertiary flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> One-time · inc GST
                        </div>
                        <div className="text-sm font-bold text-brand-secondary mt-0.5">
                          {formatCurrency(deal.value)}
                        </div>
                      </div>
                    )}
                    {deal.monthlyRecurring != null && deal.monthlyRecurring > 0 && (
                      <div className="rounded-lg border border-theme-soft px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-theme-tertiary flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Monthly · inc GST
                        </div>
                        <div className="text-sm font-bold text-emerald-500 mt-0.5">
                          {formatCurrency((deal.monthlyRecurring * (1 + getTenantTaxRateSync())))}/mo
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected services */}
                  {(deal.selectedServices ?? []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] uppercase tracking-wide text-theme-tertiary mb-1.5">
                        Services
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {(deal.selectedServices ?? []).map((s) => (
                          <span
                            key={s}
                            className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-surface-hover text-theme-secondary"
                          >
                            {String(s).replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent activity */}
                  {activity.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-theme-tertiary mb-1.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Recent activity
                      </p>
                      <ul className="space-y-1.5">
                        {activity.map((entry, i) => (
                          <li
                            key={`${entry.timestamp ?? ''}-${i}`}
                            className="text-xs text-theme-secondary border-l-2 border-theme-soft pl-2.5 py-0.5"
                          >
                            <span className="text-theme-primary">{entry.details || entry.action || 'Activity'}</span>
                            {entry.timestamp && (
                              <span className="text-theme-tertiary ml-1.5">
                                · {new Date(entry.timestamp).toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-theme-soft flex items-center justify-between gap-3 flex-shrink-0">
              <button
                onClick={onClose}
                className="text-xs font-medium text-theme-tertiary hover:text-theme-primary transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleOpenWorkspace}
                disabled={!deal}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-secondary text-white text-xs font-semibold hover:bg-brand-secondary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Open full workspace
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
